import styled from '@emotion/styled';
import { Fragment } from 'react';

import { authStore } from '../../store/AuthStore';

import { useI18n } from '../../hooks/useI18nFormatters';
import { useWebAppSettings } from '../../store/web-app-settings/useWebAppSettings';

import glyphStyles from '../../components/common/Glyph.module.css';
import { scrollbars } from '../../theme/elements/scrollbars';
import { su } from '../../theme/functions/su';
import { glyph } from '../../theme/glyph';
import { motion } from '../../theme/motion';
import { theme } from '../../theme/theme';

import { Glyph } from '../../components/common/Glyph';
import { Icon } from '../../components/common/Icon';
import { ExternalLink } from '../../components/common/link/ExernalLink';
import { Menu, MenuItem } from '../../components/common/Menu';
import { Separator } from '../../components/common/Separator';
import { Heading4Medium } from '../../components/common/text/Heading4Medium';

export function UserMenuOverlay(props) {
  const { settings } = useWebAppSettings();

  const { i18n } = useI18n();
  const size = 32;

  return (
    <S.Root>
      <S.Title>{props.cloudUser?.fullname ?? props.userMe?.name}</S.Title>

      {import.meta.env.MODE !== 'self-hosted' && (
        <Fragment>
          <S.Link url="https://homey.app/account/">
            {i18n.messageFormatter('header.user.account')}
            <Glyph
              mirrorRTL={true}
              source={glyph.core.squareArrowUpRight}
              color={theme.color.highlight}
              size={theme.icon.size_small}
            />
          </S.Link>

          <Separator mx={20} my={10} />

          <Menu
            onAction={(key) => {
              props.onHomeySelect({ homeyId: key });
            }}
            aria-label="Homey Select"
          >
            {props.cloudUser?.homeys?.map((homey) => {
              const isSelectedHomey = props.selectedHomey?.id === homey.id;

              return (
                <MenuItem key={homey.id} textValue={homey.name}>
                  <S.ActionContainer>
                    <Icon
                      source={
                        settings.theme.darkMode ? homey.modelImage?.dark : homey.modelImage?.light
                      }
                      size={`${size}px`}
                    />
                    <S.ActionName title={homey.name} data-style-bold={isSelectedHomey}>
                      {homey.name}
                    </S.ActionName>
                  </S.ActionContainer>
                </MenuItem>
              );
            })}
          </Menu>
        </Fragment>
      )}

      <Separator mx={20} my={10} />

      <Menu
        aria-label="Additional Actions"
        onAction={() => {
          authStore.logout().catch(console.error);
        }}
      >
        <MenuItem key="LOG_OUT" textValue={i18n.messageFormatter('header.user.logout')}>
          <S.ActionContainer>
            <S.ExitActionName>{i18n.messageFormatter('header.user.logout')}</S.ExitActionName>
            <S.ExitIcon
              mirrorRTL={true}
              source={glyph.core.doorArrowRight}
              color={theme.color.icon_light}
              size={theme.icon.size_medium}
            />
          </S.ActionContainer>
        </MenuItem>
      </Menu>
    </S.Root>
  );
}

function S() {}
UserMenuOverlay.S = S;

S.Root = styled.div`
  ${scrollbars.dark}
  max-height: 460px;
  padding: ${su(1)};
  overflow-y: auto;
`;

S.Title = styled(Heading4Medium)`
  padding: ${su(1)} ${su(1)} 0;
`;

S.Link = styled(ExternalLink)`
  display: flex;
  align-items: center;
  margin: ${su(1)} ${su(1)};
  text-decoration: none;
  outline: 0;

  .${glyphStyles.root} {
    margin-inline-start: 5px;
  }

  &:hover {
    text-decoration: underline;
  }
`;

S.ActionName = styled.span`
  display: block;
  margin-inline-start: ${su(1)};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &[data-style-bold='true'] {
    font-weight: ${theme.fontWeight.bold};
  }
`;

S.ExitIcon = styled(Glyph)`
  transition: ${motion.transition.micro};
`;

S.ExitActionName = styled(S.ActionName)`
  margin-inline-start: 0;
  flex: 1 1 auto;
`;

S.ActionContainer = styled.div`
  display: flex;
  align-items: center;
  min-height: ${su(5)};
  padding: ${su(1, 1)};

  &:hover {
    ${S.ExitIcon} {
      color: ${theme.color.icon_light_hover};
    }
  }
`;
